import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import {
  ArrowLeft,
  Spinner,
  Package,
  MapPin,
  Calendar,
  FileText,
  User,
  Truck,
  Anchor,
  Airplane,
  FilePdf
} from '@phosphor-icons/react'
import api from '../../services/api'
import { generateQuotePdfBlob, QuoteExportData } from '../../utils/quotePdfCanvas'
import { formatDateBR } from '../../lib/date'
import { DocumentViewer } from '../../components/DocumentViewer'

interface QuoteData {
  id: string
  tx_control: string
  status: string
  year: number
  incoterm: string
  origin: {
    location: { city: string; state?: string; country: string; zip_code?: string }
    contact_name?: string
    contact_phone?: string
    collect_date?: string
  }
  destination: {
    location: { city: string; state?: string; country: string; zip_code?: string }
    contact_name?: string
    contact_phone?: string
    delivery_date?: string
  }
  pickup_location?: {
    location: { city: string; state?: string; country: string; zip_code?: string }
  }
  goods_description?: string
  goods_ncm?: string
  goods_ncm_list?: string[]
  goods_special: boolean
  goods_type?: string
  volume_items?: Array<{
    type: string
    quantity: number
    weight: number
    dimensions?: { length: number; width: number; height: number; unit: string }
    description?: string
    ncm?: string
  }>
  documents?: Array<{ name: string; url: string; type?: string }>
  created_at: string
  updated_at: string
  modality_id: string
  modality_name: string
  modality_type: string
  volume_name?: string
  client_reference?: string
  quote_deadline?: string
  request_platform_agents?: boolean
  request_insurance_quote?: boolean
  shipper_name?: string
  shipper_trading_name?: string
  shipper_cnpj?: string
  shipper_logo_url?: string
}

const MODALITY_LABELS: Record<string, string> = {
  maritimo: 'Maritimo',
  aereo: 'Aereo',
  terrestre: 'Terrestre'
}

const OPERATION_LABELS: Record<string, string> = {
  import: 'Importacao',
  export: 'Exportacao'
}

const CARGO_TYPE_LABELS: Record<string, string> = {
  geral: 'Carga Geral',
  empilhavel: 'Empilhavel',
  perigosa: 'Perigosa'
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
  active: { label: 'Ativa', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Concluida', color: 'bg-green-100 text-green-700' },
  canceled: { label: 'Cancelada', color: 'bg-red-100 text-red-700' }
}

export default function QuoteViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showError } = useDialog()

  const [loading, setLoading] = useState(true)
  const [quote, setQuote] = useState<QuoteData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [generatingPdf, setGeneratingPdf] = useState(false)

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfFileName, setPdfFileName] = useState<string>('')

  useEffect(() => {
    fetchQuoteData()
  }, [id])

  async function fetchQuoteData() {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/quotes/${id}/export`)
      setQuote(response.data.quote)
    } catch (err: any) {
      console.error('Error fetching quote:', err)
      setError(err.response?.data?.error || 'Erro ao carregar cotacao')
    } finally {
      setLoading(false)
    }
  }

  async function handleViewPdf() {
    if (!quote) return

    try {
      setGeneratingPdf(true)
      console.log('[QuoteViewPage] Starting PDF generation with html2canvas...')
      const { url, fileName } = await generateQuotePdfBlob(quote as QuoteExportData)
      console.log('[QuoteViewPage] PDF generated:', fileName)
      setPdfUrl(url)
      setPdfFileName(fileName)
      setPdfViewerOpen(true)
    } catch (err) {
      console.error('[QuoteViewPage] Error generating PDF:', err)
      showError('Erro', 'Erro ao gerar PDF. Tente novamente.')
    } finally {
      setGeneratingPdf(false)
    }
  }

  function handleClosePdfViewer() {
    setPdfViewerOpen(false)
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl)
      setPdfUrl(null)
    }
  }

  function getModalityIcon(modality: string) {
    switch (modality) {
      case 'maritimo':
        return <Anchor size={24} className="text-blue-600" weight="duotone" />
      case 'aereo':
        return <Airplane size={24} className="text-sky-600" weight="duotone" />
      case 'terrestre':
        return <Truck size={24} className="text-amber-600" weight="duotone" />
      default:
        return <Package size={24} className="text-gray-600" weight="duotone" />
    }
  }

  function formatLocation(location: { city: string; state?: string; country: string; zip_code?: string }): string {
    const parts = [location.city]
    if (location.state) parts.push(location.state)
    parts.push(location.country)
    return parts.join(', ')
  }

  function formatWeight(weight: number): string {
    if (weight >= 1000) {
      return `${(weight / 1000).toFixed(2)} ton`
    }
    return `${weight} kg`
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando cotacao...</span>
        </div>
      </AppLayout>
    )
  }

  if (error || !quote) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Cotacao nao encontrada'}</p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} className="mr-2" />
              Voltar
            </Button>
          </div>
        </div>
      </AppLayout>
    )
  }

  const statusConfig = STATUS_LABELS[quote.status] || { label: quote.status, color: 'bg-gray-100 text-gray-700' }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">
                    {quote.tx_control || `#${quote.id.slice(0, 8)}`}
                  </h1>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Visualizacao da Cotacao
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleViewPdf}
                disabled={generatingPdf}
              >
                {generatingPdf ? (
                  <>
                    <Spinner size={16} className="mr-2 animate-spin" />
                    Gerando PDF...
                  </>
                ) : (
                  <>
                    <FilePdf size={16} className="mr-2" />
                    Visualizar PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quote Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {quote.shipper_logo_url ? (
                <img
                  src={quote.shipper_logo_url}
                  alt={quote.shipper_name}
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                  {(quote.shipper_trading_name || quote.shipper_name || 'E')
                    .split(' ')
                    .map((word: string) => word[0])
                    .join('')
                    .toUpperCase()
                    .substring(0, 2)}
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {quote.shipper_trading_name || quote.shipper_name || 'Empresa Solicitante'}
                </h2>
                {quote.shipper_trading_name && quote.shipper_name && (
                  <p className="text-sm text-gray-500">{quote.shipper_name}</p>
                )}
                {quote.shipper_cnpj && (
                  <p className="text-sm text-gray-500">CNPJ: {quote.shipper_cnpj}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">{quote.tx_control}</p>
              {quote.client_reference && (
                <p className="text-sm text-gray-600 mt-1">
                  Ref: {quote.client_reference}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Criada em {formatDateBR(quote.created_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Route Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-emerald-600" weight="duotone" />
            Rota
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Origin */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                <span className="text-sm font-medium text-gray-500">Origem</span>
              </div>
              <p className="font-semibold text-gray-900">{formatLocation(quote.origin.location)}</p>
              {quote.origin.contact_name && (
                <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                  <User size={14} />
                  {quote.origin.contact_name}
                  {quote.origin.contact_phone && ` - ${quote.origin.contact_phone}`}
                </p>
              )}
              {quote.origin.collect_date && (
                <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                  <Calendar size={14} />
                  Coleta: {formatDateBR(quote.origin.collect_date)}
                </p>
              )}
            </div>

            {/* Destination */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-sm font-medium text-gray-500">Destino</span>
              </div>
              <p className="font-semibold text-gray-900">{formatLocation(quote.destination.location)}</p>
              {quote.destination.contact_name && (
                <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                  <User size={14} />
                  {quote.destination.contact_name}
                  {quote.destination.contact_phone && ` - ${quote.destination.contact_phone}`}
                </p>
              )}
              {quote.destination.delivery_date && (
                <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                  <Calendar size={14} />
                  Entrega: {formatDateBR(quote.destination.delivery_date)}
                </p>
              )}
            </div>
          </div>

          {/* Pickup location if exists */}
          {quote.pickup_location && (
            <div className="mt-4 bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-amber-500 rounded-full" />
                <span className="text-sm font-medium text-gray-500">Local de Coleta</span>
              </div>
              <p className="font-semibold text-gray-900">{formatLocation(quote.pickup_location.location)}</p>
            </div>
          )}
        </div>

        {/* Modality Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            {getModalityIcon(quote.modality_name)}
            Modalidade
          </h3>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg font-medium">
              {getModalityIcon(quote.modality_name)}
              {MODALITY_LABELS[quote.modality_name] || quote.modality_name}
            </span>
            <span className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">
              {OPERATION_LABELS[quote.modality_type] || quote.modality_type}
            </span>
            {quote.volume_name && (
              <span className="inline-flex items-center px-4 py-2 bg-purple-50 text-purple-700 rounded-lg font-medium">
                {quote.volume_name}
              </span>
            )}
            <span className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium">
              {quote.incoterm}
            </span>
          </div>
        </div>

        {/* Cargo Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package size={20} className="text-blue-600" weight="duotone" />
            Carga
          </h3>

          {/* Description and details — goods description intentionally hidden
              here (Plane #2): long descriptions made the scrollbar excessive. */}
          <div className="space-y-3 mb-6">
            {(quote.goods_ncm_list && quote.goods_ncm_list.length > 0 ? true : !!quote.goods_ncm) && (
              <div>
                <span className="text-sm text-gray-500">NCM</span>
                {quote.goods_ncm_list && quote.goods_ncm_list.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {quote.goods_ncm_list.map((ncm, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-sm font-mono"
                      >
                        {ncm}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-900 font-mono">{quote.goods_ncm}</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {quote.goods_type && (
                <span className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full">
                  {CARGO_TYPE_LABELS[quote.goods_type] || quote.goods_type}
                </span>
              )}
              {quote.goods_special && (
                <span className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-full">
                  Manuseio Especial
                </span>
              )}
            </div>
          </div>

          {/* Volumes Table */}
          {quote.volume_items && quote.volume_items.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Volumes</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Qtd</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Peso</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Dimensoes</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">NCM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.volume_items.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium text-gray-900">{item.type}</td>
                        <td className="py-3 px-4 text-center text-gray-700">{item.quantity}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatWeight(item.weight)}</td>
                        <td className="py-3 px-4 text-center text-gray-700">
                          {item.dimensions
                            ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}`
                            : '-'
                          }
                        </td>
                        <td className="py-3 px-4 text-gray-700 font-mono text-xs">
                          {item.ncm || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-3 px-4 text-gray-900">Total</td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {quote.volume_items.reduce((sum, item) => sum + item.quantity, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        {formatWeight(quote.volume_items.reduce((sum, item) => sum + (item.weight * item.quantity), 0))}
                      </td>
                      <td className="py-3 px-4"></td>
                      <td className="py-3 px-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Documents Section */}
        {quote.documents && quote.documents.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-purple-600" weight="duotone" />
              Documentos Anexados
            </h3>
            <div className="space-y-2">
              {quote.documents.map((doc, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <FileText size={20} className="text-gray-400" />
                  <span className="text-sm text-gray-700">{doc.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Options Section */}
        {(quote.quote_deadline || quote.request_platform_agents || quote.request_insurance_quote) && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Opcoes da Cotacao</h3>
            <div className="space-y-2">
              {quote.quote_deadline && (
                <p className="text-sm text-gray-700 flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  Prazo para receber ofertas: {formatDateBR(quote.quote_deadline)}
                </p>
              )}
              {quote.request_platform_agents && (
                <p className="text-sm text-gray-700">
                  Receber cotacoes de agentes parceiros Eximtech
                </p>
              )}
              {quote.request_insurance_quote && (
                <p className="text-sm text-gray-700">
                  Solicitar cotacao de seguro
                </p>
              )}
            </div>
          </div>
        )}

      </main>

      {/* PDF Viewer Modal */}
      {pdfUrl && (
        <DocumentViewer
          isOpen={pdfViewerOpen}
          onClose={handleClosePdfViewer}
          url={pdfUrl}
          fileName={pdfFileName}
          mimeType="application/pdf"
        />
      )}
    </AppLayout>
  )
}
